import {
  Box,
  Button,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import React from "react";

import { trackEvent } from "utils/tracking";

const HOW_TO_USE_RESOURCES = [
  {
    icon: "fa-solid fa-notebook",
    color: "secondary.main",
    title: "Getting Started",
    description: "Learn how to use Bento to pitch brands and land deals.",
    actionText: "Read More",
    actionLink: "https://onbento.notion.site/",
  },
  {
    icon: "fa-solid fa-notebook",
    color: "secondary.main",
    title: "How to Use Recommendations",
    description:
      "See how to make the most of your personalized recommendations.",
    actionText: "Read More",
    actionLink: "https://onbento.notion.site/how-to-use-recommendations",
  },
  {
    icon: "fa-solid fa-notebook",
    color: "secondary.main",
    title: "How to Use Inbox",
    description:
      "Learn how to manage all your brand conversations in one place.",
    actionText: "Read More",
    actionLink: "https://onbento.notion.site/how-to-use-inbox",
  },

  {
    icon: "fa-solid fa-notebook",
    color: "secondary.main",
    title: "How to Use Campaigns",
    description:
      "Learn how to use effectively use Campaigns to schedule emails in bulk.",
    actionText: "Read More",
    actionLink: "https://onbento.notion.site/how-to-use-campaigns",
  },
  {
    icon: "fa-solid fa-notebook",
    color: "secondary.main",
    title: "How to Get Full Value of Pro Subscription",
    description:
      "Learn how to make the most of your Pro plan with higher email limits, advanced filters, and access to verified brand contacts.",
    actionText: "Read More",
    actionLink: "https://onbento.notion.site/get-full-value-pro",
  },
  {
    icon: "fa-solid fa-notebook",
    color: "secondary.main",
    title: "How to Get Full Value of Virtual Assistant Subscription",
    description:
      "Learn how to get the most out of your Virtual Assistant plan by combining automation with tailored pitches.",
    actionText: "Read More",
    actionLink: "https://onbento.notion.site/get-full-value-va",
  },
  {
    icon: "fa-solid fa-notebook",
    color: "secondary.main",
    title: "How to Use Your Bento Metrics",
    description:
      "See what summary metrics we provide and how to use them to improve your outreach.",
    actionText: "Read More",
    actionLink: "https://hatchways.notion.site/how-to-use-metrics",
  },
  {
    icon: "fa-solid fa-notebook",
    color: "secondary.main",
    title: "How to Get Bento Credits",
    description:
      "Learn how you can get Bento credits by referring friends to the platform!",
    actionText: "Read More",
    actionLink: "https://onbento.notion.site/how-to-get-bento-credits",
  },
];

const PITCH_RESOURCES = [
  {
    icon: "fa-solid fa-notebook",
    color: "secondary.main",
    title: "What is Pitching",
    description:
      "A practical guide to help content creators land brand deals—covering everything from finding the right contacts to writing emails and growing your first partnerships.",
    actionText: "Read More",
    actionLink: "https://www.onbento.com/post/sales-partnerships-manual",
  },
  {
    icon: "fa-solid fa-notebook",
    color: "secondary.main",
    title: "How to Pitch",
    description:
      "Learn how to find and approach brands that align with your audience—balancing big names with emerging opportunities.",
    actionText: "Read More",
    actionLink: "https://www.onbento.com/post/sales-manual-start-selling",
  },
  {
    icon: "fa-solid fa-notebook",
    color: "secondary.main",
    title: "How to Price & Negotiate",
    description:
      "Learn how to price your work strategically in the early stages—balancing competitive rates with long-term growth and partnership potential.",
    actionText: "Read More",
    actionLink:
      "https://www.onbento.com/post/sales-manual-pricing-and-contacts",
  },
  {
    icon: "fa-solid fa-notebook",
    color: "secondary.main",
    title: "How to Build a Scalable Process",
    description:
      "Learn how to showcase your past work and build long-term relationships with brands.",
    actionText: "Read More",
    actionLink:
      "https://www.onbento.com/post/sales-manual-building-a-sustainable-process",
  },
  {
    icon: "fa-solid fa-notebook",
    color: "secondary.main",
    title: "How to Prevent Your Email from Going Into Spam or Getting Blocked",
    description:
      "Learn how to keep your emails out of spam and avoid getting blocked with these simple best practices.",
    actionText: "Read More",
    actionLink: "https://onbento.notion.site/prevent-getting-blocked",
  },
];

const REFINE_RESOURCES = [
  {
    icon: "fa-solid fa-video",
    color: "primary.main",
    title: "How to write a better Pitch Email",
    description:
      "Learn how to craft pitch emails that stand out, feel personal, and get responses from brands.",
    actionText: "Watch More",
    actionLink: "https://www.loom.com/share/7e510141384149e7962f80e412882a56",
  },
  {
    icon: "fa-solid fa-video",
    color: "primary.main",
    title: "Rewriting Creators Pitch Playlist",
    description:
      "Watch real creator pitches get rewritten for clarity, tone, and impact—so you can apply the same tips to your own outreach.",
    actionText: "Watch More",
    actionLink:
      "https://www.youtube.com/watch?v=KRU62IuHbaA&list=PLuAqaH-DOuVjL-wW5rEWPf3E-jYmPJ26N&index=8&ab_channel=Jaclyn",
  },
];

const CLOSING_DEALS_RESOURCES = [
  {
    icon: "fa-solid fa-notebook",
    color: "secondary.main",
    title: "Optimizing your pitching process",
    description:
      "Learn how Bento's CEO pitched 1K brands in 4 months as a new creator—with no audience.",
    actionText: "Read More",
    actionLink:
      "https://www.onbento.com/post/what-i-learned-from-pitching-to-1k-brands",
  },
  {
    icon: "fa-solid fa-notebook",
    color: "secondary.main",
    title: "How to Close a Brand Deal",
    description:
      "Learn the step-by-step process of turning brand interest into a signed deal—from the first reply to follow-ups and final negotiation.",
    actionText: "Read More",
    actionLink: "https://onbento.notion.site/how-to-close-a-brand-deal?pvs=4",
  },
];

const ALL_RESOURCES = [
  {
    title: "How to Use Bento",
    resources: HOW_TO_USE_RESOURCES,
  },
  {
    title: "Pitching 101",
    resources: PITCH_RESOURCES,
  },
  {
    title: "Refining Your Pitch",
    resources: REFINE_RESOURCES,
  },
  {
    title: "Closing Brand Deals",
    resources: CLOSING_DEALS_RESOURCES,
  },
];

const MINIMAL_RESOURCES = [
  {
    title: "How to Use Bento",
    resources: HOW_TO_USE_RESOURCES,
  },
];

export default function ResourcesHub() {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const resources = isExpanded ? ALL_RESOURCES : MINIMAL_RESOURCES;
  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
        backgroundColor: "white",
        borderRadius: 4,
      }}
    >
      <Grid
        container
        justifyContent="space-between"
        sx={{ cursor: "pointer" }}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <Typography variant="h5" sx={{ pl: 2, mb: 2 }}>
          Resources Hub
        </Typography>
      </Grid>

      {resources.map((section) => (
        <React.Fragment key={section.title}>
          <Typography sx={{ pl: 2, mb: 1, color: "green.main", fontSize: 16 }}>
            {section.title}
          </Typography>
          <List dense sx={{ pt: 0, pb: 2 }}>
            {section.resources.map((resource: any, index: number) => (
              <Box key={`${resource.title}-${index}`}>
                <Divider sx={{ mx: -2, borderColor: "purple.light" }} />
                <ListItem dense key={resource.title} alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Typography gutterBottom>{resource.title}</Typography>
                    }
                    secondary={
                      <>
                        <Typography
                          gutterBottom
                          variant="body2"
                          sx={{ fontSize: 11 }}
                          color="textSecondary"
                        >
                          {resource.description}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: 11 }}>
                          <Link
                            sx={{
                              textDecoration: "none",
                              color: "purple.main",
                            }}
                            href={resource.actionLink}
                            target="_blank"
                            rel="noreferrer"
                            onClick={() => {
                              trackEvent("Resources Hub Read More Clicked", {
                                resource: resource.title,
                              });
                            }}
                          >
                            <strong>{resource.actionText}</strong>
                            {resource.actionText?.includes("Read") ? (
                              <Box
                                component="i"
                                className="fa-regular fa-arrow-up-right-from-square"
                                sx={{ ml: 0.5 }}
                              />
                            ) : (
                              <Box
                                component="i"
                                className="fa-regular fa-eye"
                                sx={{ ml: 0.5 }}
                              />
                            )}
                          </Link>
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              </Box>
            ))}
          </List>

          {!isExpanded && (
            <Button size="small" onClick={() => setIsExpanded((prev) => !prev)}>
              View All
              <Box
                component="i"
                className={`fa-regular fa-chevron-down`}
                sx={{ ml: 1 }}
              />
            </Button>
          )}
        </React.Fragment>
      ))}
    </Box>
  );
}
